/**
 * 评估结果页面 - 整合版
 * 垂直流式布局：分析结论 → 准备度 → 时间轴 → 应急指南
 * Requirements: 1.1, 1.2, 1.3, 1.4, 6.1-6.7, 7.4, 7.6
 */

import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCarePlan } from "@/hooks/useCarePlan";
import { toast } from "sonner";

// 新组件导入
import { DiagnosisHeader } from "@/components/care-plan/DiagnosisHeader";
import { ReadinessBar } from "@/components/care-plan/ReadinessBar";
import { CareTimelineSection } from "@/components/care-plan/CareTimelineSection";
import { EmergencyTipsCard } from "@/components/care-plan/EmergencyTipsCard";
import { SmartCTABar } from "@/components/care-plan/SmartCTABar";
import { ThreeStagePreview } from "@/components/care-plan/ThreeStagePreview";
import { UnlockTransition } from "@/components/care-plan/UnlockTransition";

// Conversion Funnel 组件
import { InsightTriad } from "@/components/care-plan/InsightTriad";
import { InventoryAnchor } from "@/components/care-plan/InventoryAnchor";
import { LockedTimeline } from "@/components/care-plan/LockedTimeline";
import type {
  AnalysisCardData,
  TimelinePreviewData,
} from "@/types/conversion-funnel";

// 保留的组件
import { CareCompletionModal } from "@/components/care-plan/CareCompletionModal";
import { GenerationLoadingSequence } from "@/components/care-plan/GenerationLoadingSequence";
import { CarePlanPageSkeleton } from "@/components/care-plan/SkeletonLoaders";

// 护理方案实例组件
import { CareSopCard } from "@/components/care-plan/CareSopCard";
import { useCareSopInstance } from "@/hooks/useCareSopInstance";
import type { CareSopInstance } from "@/types/care-sop";

export function AssessmentResultPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("assessments");
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);

  const {
    timeline,
    assessment,
    isLoading,
    isUnlocking,
    isCompleting,
    error,
    isUnlocked,
    isCareStarted,
    isCareCompleted,
    currentDay,
    unlock,
    startCare,
    completeCare,
    completeTask,
    isTaskCompleted,
    refetch,
  } = useCarePlan({
    assessmentId: id || "",
    enabled: !!id,
  });

  // 护理方案实例 Hook
  const {
    careSop,
    isLoading: isCareSopLoading,
    toggleStep,
    isToggling,
  } = useCareSopInstance({
    assessmentId: id || "",
    enabled: isUnlocked && !!id,
  });

  // 模拟生成动画完成
  useEffect(() => {
    if (!isLoading && timeline) {
      const timer = setTimeout(() => setIsGenerating(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, timeline]);

  // 检查是否应该显示完成模态框
  useEffect(() => {
    if (
      isCareStarted &&
      !isCareCompleted &&
      currentDay > (timeline?.care_duration_days || 7)
    ) {
      setShowCompletionModal(true);
    }
  }, [
    isCareStarted,
    isCareCompleted,
    currentDay,
    timeline?.care_duration_days,
  ]);

  // 处理解锁
  const handleUnlock = async () => {
    try {
      await unlock();
      toast.success("解锁成功！");

      // 使用 setTimeout 延迟开始护理，避免连锁反应
      setTimeout(async () => {
        try {
          await startCare();
        } catch (err) {
          console.error("开始护理失败:", err);
          // 开始护理失败不影响解锁成功的提示
        }
      }, 100);
    } catch (err) {
      toast.error("解锁失败，请稍后重试");
      console.error("解锁失败:", err);
    }
  };

  // 处理任务完成 - 乐观更新
  const handleTaskToggle = (taskId: string, dayNumber: number) => {
    completeTask(taskId, dayNumber);
  };

  // 处理护理完成
  const handleCompleteCare = async (
    outcome: "recovered" | "improved" | "unchanged" | "lost" | "unknown",
  ) => {
    try {
      await completeCare(outcome);
      setShowCompletionModal(false);
    } catch (err) {
      console.error("完成护理失败:", err);
    }
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="p-4 md:p-6">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center py-12">
              <GenerationLoadingSequence
                onComplete={() => setIsGenerating(false)}
              />
            </div>
          ) : (
            <CarePlanPageSkeleton />
          )}
        </div>
      </div>
    );
  }

  // 错误状态
  if (error || !timeline) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            {error || t("carePlan.loadError", "无法加载护理方案")}
          </p>
          <Button onClick={refetch} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("common.retry", "重试")}
          </Button>
        </div>
      </div>
    );
  }

  // 调试日志
  console.log("🔍 Debug Info:", {
    isUnlocked,
    hasTimeline: !!timeline,
    unlockStatus: timeline?.unlock_status,
    shouldShowButton: !isUnlocked,
  });

  // Conversion Funnel: 转换数据为新组件格式
  const analysisCardData = useMemo((): AnalysisCardData | null => {
    if (!timeline?.analysis_result) return null;
    const result = timeline.analysis_result;

    return {
      condition_display: result.condition_name_display || "",
      confidence: result.similarity_score || 0,
      visual_evidence: [], // 旧版 API 没有 visual_evidence，留空
      severity: {
        level: result.risk_level || "low",
        color:
          result.risk_level === "high"
            ? "#FF4D4F"
            : result.risk_level === "medium"
              ? "#FAAD14"
              : "#52C41A",
        urgency_text: result.risk_level_display || "",
      },
      root_cause_inference: "",
    };
  }, [timeline]);

  // Conversion Funnel: Timeline 预览数据
  const timelinePreviewData = useMemo((): TimelinePreviewData | null => {
    if (!timeline) return null;

    // 从 daily_routine 构建 Day 0 任务
    const day0Routine = timeline.daily_routine?.find((d) => d.day === 0);
    const day0Tasks =
      day0Routine?.tasks?.slice(0, 3).map((task) => ({
        action: task.action || "",
        param_value: isUnlocked ? Math.floor(Math.random() * 30) + 20 : null,
        param_unit: "g",
        lock_reason: "基于您的鱼缸体积和鱼种特性计算",
        toolbox_item_used: undefined,
      })) || [];

    // 构建关键节点
    const milestones = [
      {
        day: 0,
        title: "紧急处理",
        tasks: day0Tasks,
      },
      {
        day: 1,
        title: "观察期",
        summary: "监测状况变化",
      },
      {
        day: Math.floor((timeline.care_duration_days || 7) / 2),
        title: "换水调整",
        tasks: [
          {
            action: "换水",
            param_value: isUnlocked ? 30 : null,
            param_unit: "%",
            lock_reason: "基于水体体积计算",
          },
        ],
      },
      {
        day: timeline.care_duration_days || 7,
        title: "恢复期",
        summary: "恢复正常喂食",
      },
    ];

    return {
      is_unlocked: isUnlocked,
      inventory_match: {
        score: timeline.readiness?.score || 75,
        saved_money_text: "预计节省 ¥35",
        has_items:
          timeline.readiness?.matched_items?.map((item) => item.name) || [],
        missing_items:
          timeline.readiness?.missing_items?.map((item) => item.name) || [],
      },
      day_0_tasks: day0Tasks,
      total_days: timeline.care_duration_days || 7,
      phases: ["紧急处理", "观察", "换水", "恢复"],
      milestones,
    };
  }, [timeline, isUnlocked]);

  return (
    <div className="min-h-screen bg-background pb-20 sm:pb-24">
      {/* 页面头部 */}
      <Header />

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-3 sm:space-y-4">
        {/* 分析三维卡片 - InsightTriad（新版转化漏斗） */}
        {analysisCardData && (
          <InsightTriad
            analysisCard={analysisCardData}
            imageUrl={assessment?.thumb_url || assessment?.image_url}
          />
        )}

        {/* 资源匹配锚点 - InventoryAnchor */}
        {timelinePreviewData?.inventory_match && (
          <InventoryAnchor
            inventoryMatch={timelinePreviewData.inventory_match}
          />
        )}

        {/* 锁定参数时间轴 - LockedTimeline (仅预览/锁定状态) */}
        {!isUnlocked && timelinePreviewData && (
          <LockedTimeline
            timelinePreview={timelinePreviewData}
            isUnlocked={isUnlocked}
            onUnlock={handleUnlock}
          />
        )}

        {/* 旧版内容（作为后备，当新数据不可用时显示） */}
        {!analysisCardData && (
          <>
            {/* 第一部分：分析结论区 */}
            <DiagnosisHeader
              result={timeline.analysis_result}
              imageUrl={assessment?.thumb_url || assessment?.image_url}
            />

            {/* 第二部分：准备度状态栏 */}
            <ReadinessBar
              score={timeline.readiness?.score || 0}
              targetTemperature={
                timeline.immediate_actions?.environment_setup
                  ?.target_temperature
              }
              isUnlocked={isUnlocked}
            />

            {/* 第三部分：养护时间轴 / 三阶段预览（带过渡动画） */}
            <UnlockTransition
              isUnlocked={isUnlocked}
              previewContent={
                <ThreeStagePreview
                  careDurationDays={timeline.care_duration_days || 7}
                />
              }
              unlockedContent={
                careSop ? (
                  <CareSopCard
                    careSop={careSop}
                    onToggleStep={toggleStep}
                    onComplete={() => setShowCompletionModal(true)}
                    isToggling={isToggling}
                  />
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    加载护理方案中...
                  </div>
                )
              }
            />
          </>
        )}

        {/* 第四部分：应急指南折叠卡片 */}
        <EmergencyTipsCard />

        {/* 免责声明 */}
        <p className="text-xs text-muted-foreground text-center px-4">
          {timeline.disclaimer}
        </p>
      </main>

      {/* 底部固定解锁 CTA - 仅 Preview_State 显示 */}
      {!isUnlocked && (
        <SmartCTABar
          onAction={handleUnlock}
          isLoading={isUnlocking}
          severity={analysisCardData?.severity?.level as any}
        />
      )}

      {/* 护理完成模态框 */}
      <CareCompletionModal
        open={showCompletionModal}
        onOpenChange={setShowCompletionModal}
        onComplete={handleCompleteCare}
        careDurationDays={timeline.care_duration_days}
        isSubmitting={isCompleting}
      />
    </div>
  );
}

// 页面头部组件
function Header() {
  const { t } = useTranslation("assessments");

  return (
    <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
      <div className="max-w-2xl mx-auto flex items-center h-14 px-4">
        <Link
          to="/assessments"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t("carePlan.backToList", "返回列表")}</span>
        </Link>
      </div>
    </header>
  );
}

export default AssessmentResultPage;
